import "./user-registration.scss";
import { track } from "jiffy-analytics";
import initPasswordStrengthMeter from "common/jiffy/password-strength-meter";

const SELECTORS = {
  form: ".user-registration__form",
  email: ".js-user-registration-email",
  password: "#spree_user_password",
  passwordConfirmation: ".js-user-registration-password-confirmation",
  submitButton: ".js-user-registration-submit-button",
  resetButton: "#user-registration-reset",
  errorContainer: "#sign-up-errors",
  errorMessage: "#sign-up-errors .flash-message__message"
};

const getElements = () => ({
  form: document.querySelector(SELECTORS.form),
  email: document.querySelector(SELECTORS.email),
  password: document.querySelector(SELECTORS.password),
  passwordConfirmation: document.querySelector(SELECTORS.passwordConfirmation),
  submitButton: document.querySelector(SELECTORS.submitButton),
  resetButton: document.querySelector(SELECTORS.resetButton),
  errorContainer: document.querySelector(SELECTORS.errorContainer),
  errorMessage: document.querySelector(SELECTORS.errorMessage)
});

const setFieldValidation = (field, message, handler) => {
  if (field)
    field.addEventListener(handler, function () {
      this.setCustomValidity(message);
    });
};

const clearFieldValidation = field => {
  if (field)
    field.addEventListener("input", function () {
      this.setCustomValidity("");
    });
};

const handleValidation = () => {
  const { email, password, passwordConfirmation } = getElements();

  setFieldValidation(email, "Please provide a valid email address", "invalid");
  setFieldValidation(
    password,
    "Please provide a password containing at least 8 characters",
    "invalid"
  );
  setFieldValidation(passwordConfirmation, "Please confirm your password", "invalid");

  clearFieldValidation(email);
  clearFieldValidation(password);
  clearFieldValidation(passwordConfirmation);
};

const showError = (message, field = null) => {
  const { errorContainer, errorMessage } = getElements();
  if (field) {
    field.setCustomValidity(message);
    field.reportValidity();
  }
  if (errorContainer && errorMessage) {
    errorMessage.innerHTML = message;
    errorContainer.classList.remove("hide");
  }
};

const hideError = () => {
  const { errorContainer } = getElements();
  if (errorContainer) errorContainer.classList.add("hide");
};

const handleSuccessResponse = response => {
  if (response.signup_event_id && response.email) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "SignUp",
      email: response.email,
      eventId: response.signup_event_id
    });
  }
  if (response.redirect_url) window.location.href = response.redirect_url;
};

const handleErrorResponse = response => {
  const { form, email, password } = getElements();
  if (!form) return;

  form.style.pointerEvents = "auto";

  if (response.error_message) showError(response.error_message);
  if (response.errors) {
    if (response.errors.email) showError(response.errors.email[0], email);
    if (response.errors.password) showError(response.errors.password[0], password);
    if (response.errors.base) showError(response.errors.base[0]);
  }

  if (email) email.focus();
};

const setFormLoadingState = isLoading => {
  const { form, submitButton } = getElements();
  if (!form || !submitButton) return;

  if (isLoading) {
    hideError();
    form.style.pointerEvents = "none";
    submitButton.disabled = true;
    submitButton.textContent = "Creating Account...";
  } else {
    form.style.pointerEvents = "auto";
    submitButton.disabled = false;
    submitButton.textContent = "Create Account";
  }
};

const submitRegistrationForm = form =>
  fetch(form.action, {
    method: "POST",
    body: new FormData(form),
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token":
        document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
    }
  }).then(response => response.json());

const handleFormSubmission = event => {
  event.preventDefault();
  event.stopPropagation();

  const form = event.target;
  setFormLoadingState(true);
  track("create_account_initiated");

  submitRegistrationForm(form)
    .then(data => {
      if (data.success || data.redirect_url) {
        handleSuccessResponse(data);
      } else {
        handleErrorResponse(data);
      }
    })
    .catch(error => {
      console.error("Error:", error);
      showError("An error occurred. Please try again.");
    })
    .finally(() => setFormLoadingState(false));
};

const setupClearErrorsOnFocus = () => {
  const { email, password, passwordConfirmation } = getElements();
  const fields = [email, password, passwordConfirmation];

  fields.forEach(field => {
    if (field) {
      field.addEventListener("focus", function () {
        this.setCustomValidity("");
        hideError();
      });
    }
  });
};

const handleFormReset = () => {
  const { form, resetButton, email, password, passwordConfirmation } = getElements();

  if (resetButton && form) {
    resetButton.addEventListener("click", event => {
      event.preventDefault();
      form.reset();
      hideError();

      const fields = [email, password, passwordConfirmation];
      fields.forEach(field => field?.setCustomValidity(""));

      if (email) email.focus();
    });
  }
};

export default function initUserRegistration() {
  const { form, email } = getElements();
  if (!form) return;

  initPasswordStrengthMeter();
  handleValidation();
  setupClearErrorsOnFocus();
  handleFormReset();
  form.addEventListener("submit", handleFormSubmission);

  if (email) setTimeout(() => email.focus(), 100);
}
